import { AlertCircle, Sparkles, Zap } from 'lucide-react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { ROUTE } from '@/config';

interface RuleMandateInfo {
  /** Human-readable rule name, e.g. "KRG-Aramex" */
  ruleName: string;
  /** Human-readable carrier name the rule mandates, e.g. "Aramex". Optional —
   *  unset when the rule mandates `cheapest` filter rather than a specific carrier. */
  carrierName?: string;
}

interface NoCarrierModalProps {
  open: boolean;
  onClose: () => void;
  recommendedCarrierSlug?: string;
  /**
   * NEW-2 — when set, render the explicit "rule mandates X but X is
   * unavailable for this route" variant instead of the generic
   * "no carriers" message. Provides Edit-Rule and Pick-Manual CTAs.
   */
  ruleMandate?: RuleMandateInfo;
  /** NEW-2 — fallback to manual carrier selection (clears rule decision). */
  onPickManual?: () => void;
  /**
   * Task-6 — when set (and `ruleMandate` is not), the generic variant names
   * the destination province in its title instead of the generic "no
   * carriers matched" copy. Pass the already-localized province NAME (e.g.
   * "Baghdad"), not the province code. Display-only — does not affect
   * eligibility or submit logic.
   */
  temperatureUnavailableProvince?: string;
}

const NoCarrierModal = ({
  open,
  onClose,
  recommendedCarrierSlug,
  ruleMandate,
  onPickManual,
  temperatureUnavailableProvince,
}: NoCarrierModalProps) => {
  const { t } = useTranslation();

  const handleGoToSettings = () => {
    window.open(ROUTE.SETTINGS_SHIPPING_RULES, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handlePickManual = () => {
    onPickManual?.();
    onClose();
  };

  // NEW-2 variant — rule explicitly mandates a carrier (or cheapest filter)
  // but pricing returned nothing for the current route. Distinct from the
  // generic "no carriers enabled" path because the merchant can fix it
  // EITHER by editing the rule OR by overriding it for this single order.
  if (ruleMandate) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        width={520}
        className='sd-scope sd-font'
      >
        <div className='flex flex-col items-center justify-center text-center gap-5 py-6 px-2'>
          <div className='h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center'>
            <Zap className='h-6 w-6 text-amber-600' strokeWidth={1.5} />
          </div>
          <div className='space-y-2'>
            <p className='text-base font-semibold text-sd-foreground'>
              {String(t('rule_mandate_unavailable_title'))}
            </p>
            <p className='text-sm text-sd-muted-foreground'>
              {String(
                t('rule_mandate_unavailable_message', {
                  ruleName: ruleMandate.ruleName,
                  carrierName: ruleMandate.carrierName ?? String(t('carriers')),
                }),
              )}
            </p>
          </div>
          <div className='flex flex-col sm:flex-row gap-3 w-full max-w-sm'>
            <button
              onClick={handleGoToSettings}
              className='flex-1 px-5 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
            >
              {String(t('edit_rule'))}
            </button>
            <button
              onClick={handlePickManual}
              className='flex-1 px-5 py-2 rounded-lg border border-sd-border text-sd-foreground text-sm font-semibold hover:bg-sd-muted transition-colors'
            >
              {String(t('pick_carrier_manually'))}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      className='sd-scope sd-font'
    >
      <div className='flex flex-col items-center justify-center text-center gap-5 py-6 px-2'>
        {/* Icon */}
        <div className='h-12 w-12 rounded-full bg-sd-muted flex items-center justify-center'>
          <AlertCircle className='h-6 w-6 text-sd-muted-foreground' strokeWidth={1.5} />
        </div>

        {/* Title */}
        <div className='space-y-1 max-w-sm'>
          <p className='text-sm font-medium text-sd-foreground'>
            {temperatureUnavailableProvince
              ? String(
                  t('temp_no_carrier_for_province', {
                    province: temperatureUnavailableProvince,
                  }),
                )
              : t('no_carrier_matched')}
          </p>
        </div>

        {/* Recommendation box */}
        {recommendedCarrierSlug ? (
          <div className='w-full max-w-sm rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-start'>
            <p className='flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-600'>
              <Sparkles className='h-3.5 w-3.5' strokeWidth={1.5} />
              {t('recommended')}
            </p>
            <p className='text-sm font-medium text-sd-foreground mt-1'>
              {t('enable_carriers_recommendation', {
                carrier: t(recommendedCarrierSlug),
              })}
            </p>
          </div>
        ) : null}

        {/* CTA */}
        <div className='flex gap-3'>
          <button
            onClick={handleGoToSettings}
            className='px-5 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
          >
            {String(t('go_to_carrier_settings'))}
          </button>
        </div>
        <p className='text-xs text-sd-muted-foreground'>
          {String(t('order_data_preserved_message', 'Your order data has been saved. You can return to continue after enabling a carrier.'))}
        </p>
      </div>
    </Modal>
  );
};

export default NoCarrierModal;
